import { useContext, useEffect, useState, type FC } from "react";
import MainLayout from "../components/MainLayout";
import { Button, Checkbox, IconButton } from "rsuite";
import { LoadingContext } from "../contexts/LoadingContext";
import { SearchContext } from "../contexts/SearchContext";
import {
  createPromotion,
  deletePromotion,
  getPromotions,
  updatePromotion,
} from "../services/sales/PromoApi";
import { errorToast } from "../utils/helper-ui";
import { PaginationResponse } from "../objects/pagination";
import { getPagination } from "../utils/helper";
import { Promotion } from "../models/sales/promotion";
import { Table, Pagination } from "rsuite";
import PlusIcon from "@rsuite/icons/Plus";
import { BsTrash3 } from "react-icons/bs";
import PromotionForm from "../components/features/sales/PromotionForm";
import { useNavigate } from "react-router-dom";
import { CheckCircleIcon, CheckIcon } from "@heroicons/react/24/outline";
import { HiXMark } from "react-icons/hi2";
const { Column, HeaderCell, Cell } = Table;
interface PromotionPageProps {}

const PromotionPage: FC<PromotionPageProps> = ({}) => {
  const nav = useNavigate();
  const { search, setSearch } = useContext(SearchContext);
  const { isLoading, setIsLoading } = useContext(LoadingContext);
  const [mounted, setMounted] = useState(false);
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(30);
  const [pagination, setPagination] = useState<PaginationResponse>();
  const [promotions, setPromotions] = useState<Promotion[]>([]);
  const [modalFormOpen, setModalFormOpen] = useState(false);
  const [selectedPromotion, setSelectedPromotion] = useState<Promotion>();

  const [checkedKeys, setCheckedKeys] = useState<string[]>([]);
  let checked = false;
  let indeterminate = false;

  if (checkedKeys.length === promotions.length) {
    checked = true;
  } else if (checkedKeys.length === 0) {
    checked = false;
  } else if (checkedKeys.length > 0 && checkedKeys.length < promotions.length) {
    indeterminate = true;
  }

  const handleCheckAll = (
    value: any,
    checked: boolean,
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const keys = checked ? promotions.map((item) => item.id) : [];
    setCheckedKeys(keys);
  };
  const handleCheck = (
    value: any,
    checked: boolean,
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const keys = checked
      ? [...checkedKeys, value as string]
      : checkedKeys.filter((item) => item !== (value as string));
    setCheckedKeys(keys);
  };

  useEffect(() => {
    setMounted(true);

    return () => {
      setMounted(false);
    };
  }, []);

  useEffect(() => {
    if (!mounted) return;
    fetchData();
  }, [mounted, search, page, size]);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const res = await getPromotions({ page, size, search });
      const resJson = await res.json();
      //   console.log(data);
      setPromotions((prev) => [...prev, ...resJson.data.items]);
      setPagination(getPagination(resJson.data));
    } catch (error) {
      errorToast(`${error}`);
    } finally {
      setIsLoading(false);
    }
  };

  const removePromotion = async (promotion: Promotion) => {
    try {
      setIsLoading(true);
      if (
        window.confirm(
          `Are you sure you want to delete promotion with name ${promotion.name}?`
        )
      ) {
        await deletePromotion(promotion.id!);
        fetchData();
      }
    } catch (error) {
      errorToast(`${error}`);
    } finally {
      setIsLoading(false);
    }
  };
  const savePromotion = async (promotion: Promotion) => {
    try {
      setIsLoading(true);
      if (selectedPromotion) {
        await updatePromotion(selectedPromotion.id!, promotion);
      } else {
        let res = await createPromotion(promotion);
        let resJson = await res.json();
        nav(`/promotion/${resJson.last_id}`);
      }
      setModalFormOpen(false);
      setSelectedPromotion(undefined);
      fetchData();
      setPromotions([]);
    } catch (error) {
      errorToast(`${error}`);
    } finally {
      setIsLoading(false);
    }
  };
  const renderHeader = () => (
    <div className="flex flex-row justify-between items-center px-2">
      <h1 className="text-2xl font-semibold">Promotion</h1>
      {/* button */}
      <div>
        <IconButton
          size="sm"
          appearance="primary"
          color="green"
          icon={<PlusIcon />}
          onClick={() => setModalFormOpen(true)}
        >
          Create Promotion
        </IconButton>
      </div>
    </div>
  );
  return (
    <MainLayout header={renderHeader()}>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr>
              <th className="px-4 py-2 text-left border bg-gray-50">
                <Checkbox
                  inline
                  checked={checked}
                  indeterminate={indeterminate}
                  onChange={handleCheckAll}
                />
              </th>
              <th className="px-4 py-2 text-left border bg-gray-50">Name</th>
              <th className="px-4 py-2 text-left border bg-gray-50" style={{ width: "50%" }}>Description</th>
              <th className="px-4 py-2 text-left border bg-gray-50">Active</th>
              <th className="px-4 py-2 text-left border bg-gray-50">Action</th>
            </tr>
          </thead>
          <tbody>
            {promotions.map((rowData, i) => (
              <tr key={i}>
                <td className="px-4 py-2 border ">
                  <Checkbox
                    value={rowData.id}
                    inline
                    onChange={handleCheck}
                    checked={checkedKeys.some((item) => item === rowData.id)}
                  />
                  {i + 1}
                </td>

                <td
                  className="hover:underline cursor-pointer font-semibold px-4 py-2 border"
                  onClick={() => {
                    nav(`/promotion/${rowData.id}`);
                  }}
                >
                  {rowData.name}
                </td>
                <td
                  className="px-4 py-2 border"
                  dangerouslySetInnerHTML={{ __html: rowData.description }}
                />
                <td className="px-4 py-2 border">
                  {rowData.is_active ? (
                    <CheckCircleIcon className="w-5 h-5 text-green-500" />
                  ) : (
                    <HiXMark className="w-5 h-5 text-red-500" />
                  )}
                </td>
                <td className="px-4 py-2 border">
                  <BsTrash3
                    className="text-red-500 hover:text-red-800"
                    size={16}
                    onClick={() => removePromotion(rowData as Promotion)}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/* <Table minHeight={700} data={promotions} onRowClick={(data, i) => {}}>
        <Column width={50} align="center" fixed>
          <HeaderCell style={{ padding: 0 }}>
            <div style={{ lineHeight: "40px" }}>
              <Checkbox
                inline
                checked={checked}
                indeterminate={indeterminate}
                onChange={handleCheckAll}
              />
            </div>
          </HeaderCell>
          <Cell style={{ padding: 0 }}>
            {(rowData) => (
              <div style={{ lineHeight: "46px" }}>
                <Checkbox
                  value={rowData.id}
                  inline
                  onChange={handleCheck}
                  checked={checkedKeys.some((item) => item === rowData.id)}
                />
              </div>
            )}
          </Cell>
        </Column>
        <Column width={70} align="center" fixed>
          <HeaderCell>No</HeaderCell>
          <Cell>
            {(rowData) => (
              <div>
                {(page - 1) * size +
                  promotions.indexOf(rowData as Promotion) +
                  1}
              </div>
            )}
          </Cell>
        </Column>

        <Column width={200} resizable fixed>
          <HeaderCell>Name</HeaderCell>
          <Cell>
            {(rowData) => (
              <div
                className="hover:underline cursor-pointer font-semibold "
                onClick={() => {
                  nav(`/promotion/${rowData.id}`);
                }}
              >
                {rowData.name}
              </div>
            )}
          </Cell>
        </Column>

        <Column width={400} resizable>
          <HeaderCell>Description</HeaderCell>
          <Cell  >
            {(rowData) => (
              <div dangerouslySetInnerHTML={{ __html: rowData.description }}/>
            )} 
          </Cell>
        </Column>
        <Column width={100} resizable>
          <HeaderCell>Active</HeaderCell>
          <Cell  >
            {(rowData) => (
              rowData.is_active ? <CheckCircleIcon className="w-5 h-5 text-green-500" /> : <HiXMark className="w-5 h-5 text-red-500" />
            )} 
          </Cell>
        </Column>

        <Column width={200} resizable>
          <HeaderCell>Action</HeaderCell>
          <Cell>
            {(rowData) => (
              <BsTrash3
                className="text-red-500 hover:text-red-800"
                size={16}
                onClick={() => removePromotion(rowData as Promotion)}
              />
            )}
          </Cell>
        </Column>
      </Table> */}
      {/* <div style={{ padding: 20 }}>
        <Pagination
          prev
          next
          first
          last
          ellipsis
          boundaryLinks
          maxButtons={5}
          size="xs"
          layout={["total", "-", "limit", "|", "pager", "skip"]}
          total={pagination?.total ?? 0}
          limitOptions={[10, 30, 50]}
          limit={pagination?.size}
          activePage={pagination?.page}
          onChangePage={setPage}
          onChangeLimit={setSize}
        />
      </div> */}
      <PromotionForm
        promotion={selectedPromotion}
        open={modalFormOpen}
        onClose={() => {
          setModalFormOpen(false);
          setSelectedPromotion(undefined);
        }}
        onSubmit={savePromotion}
      />
    </MainLayout>
  );
};
export default PromotionPage;
